import React, { useState, useMemo } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Eye, FileText, Search, RefreshCw, XCircle } from 'lucide-react';
import { flyersService } from '../../services/flyersService';
import { Button } from '../../components/ui/Button';
import { Input } from '../../components/ui/Input';
import { formatDate } from '../../utils/helpers';
import { useAuthStore } from '../../store/authStore';
import { Zalozka, ZALOZKA_POPISKY, porovnejLetaky, sedneNaZalozku } from '../../utils/letakZalozky';

/**
 * Přehled pro admina a schvalovatelskou dvojici jde napříč dodavateli a přes
 * všechny stavy - schvalovatel se tak může na leták podívat i dřív, než mu ho
 * někdo pošle ke schválení. Letáky prodejců v něm nejsou; do schvalování
 * nevstupují a backend je těmto rolím nepustí ani přes adresu.
 */
const ZALOZKY_PREHLED: Zalozka[] = ['active', 'draft', 'ceka', 'rejected', 'archive'];

export const ActiveFlyersPage: React.FC = () => {
  const navigate = useNavigate();
  const [searchQuery, setSearchQuery] = useState('');
  // ?zalozka= z dlaždice na Přehledu (schvalovatel: Aktivní, Zamítnuté).
  const [searchParams] = useSearchParams();
  const zalozkaZUrl = searchParams.get('zalozka') as Zalozka | null;
  const [activeTab, setActiveTab] = useState<Zalozka>(
    zalozkaZUrl && ZALOZKY_PREHLED.includes(zalozkaZUrl) ? zalozkaZUrl : 'active',
  );

  const { user } = useAuthStore();
  // Prodejce vidí jen aktuální nabídku - jediná záložka, žádný archiv ani cizí
  // koncepty. Své letáky má v „Moje letáky".
  const jeProdejce = user?.role === 'end_user';

  // Prodejci stačí seznam aktivních letáků. Ostatní role berou přehled ze
  // /flyers, který veze všechny stavy naráz - jedna odpověď místo dvou a navíc
  // lehčí, protože nenese sloty ani produkty.
  const { data: activeFlyers = [], isLoading: loadingActive } = useQuery({
    queryKey: ['flyers', 'active'],
    queryFn: () => flyersService.getActiveFlyers(),
    enabled: jeProdejce,
  });
  const { data: prehled = [], isLoading: loadingPrehled } = useQuery({
    queryKey: ['flyers', 'my'],
    queryFn: () => flyersService.getMyFlyers(),
    enabled: !jeProdejce,
  });

  const vsechny = jeProdejce ? activeFlyers : prehled;
  const isLoading = jeProdejce ? loadingActive : loadingPrehled;
  const isArchive = activeTab === 'archive';

  const pocty = useMemo(() => {
    const m = {} as Record<Zalozka, number>;
    for (const z of ZALOZKY_PREHLED) m[z] = vsechny.filter((f) => sedneNaZalozku(f, z)).length;
    return m;
  }, [vsechny]);

  const flyers = useMemo(
    () =>
      (jeProdejce ? [...activeFlyers] : prehled.filter((f) => sedneNaZalozku(f, activeTab))).sort(
        porovnejLetaky,
      ),
    [jeProdejce, activeFlyers, prehled, activeTab],
  );
  // Aktivni letaky spravuje schvalovatel i admin (backend pousti oba: expireFlyer
  // i assertCanRegeneratePdf). Predschvalovatel a prodejce maji pohled jen ke cteni.
  const muzeSpravovat = user?.role === 'approver' || user?.role === 'admin';
  const queryClient = useQueryClient();

  // Přegenerování PDF aktivního letáku (obnoví PDF proti aktuálním datům produktů).
  // Nemění datumy platnosti (validFrom/validTo jsou uložený snímek). Jen schvalovatel a admin.
  const regenerateMutation = useMutation({
    mutationFn: (id: string) => flyersService.generatePdf(id),
    onSuccess: () => {
      alert('PDF bylo úspěšně přegenerováno');
      queryClient.invalidateQueries({ queryKey: ['flyers'] });
    },
    onError: (error: any) => {
      alert(error?.response?.data?.message || 'Chyba při generování PDF');
    },
  });

  const handleRegeneratePdf = (id: string) => {
    if (window.confirm('Opravdu chcete přegenerovat PDF? Současné PDF bude nahrazeno.')) {
      regenerateMutation.mutate(id);
    }
  };

  // Ruční ukončení platnosti aktivního letáku (přesun do stavu "vypršelý" = Archiv).
  // Jen schvalovatel a admin; datum platnosti se nastaví na včera a produkty se uvolní k editaci.
  const expireMutation = useMutation({
    mutationFn: (id: string) => flyersService.expireFlyer(id),
    onSuccess: () => {
      alert('Platnost letáku byla ukončena – leták byl přesunut do archivu.');
      queryClient.invalidateQueries({ queryKey: ['flyers', 'active'] });
      queryClient.invalidateQueries({ queryKey: ['flyers'] });
    },
    onError: (error: any) => {
      alert(error?.response?.data?.message || 'Chyba při ukončování platnosti letáku');
    },
  });

  const handleExpire = (id: string) => {
    if (window.confirm('Opravdu chcete ukončit platnost tohoto letáku? Datum platnosti bude nastaveno na včerejší den, leták se přesune do archivu a produkty budou uvolněny k editaci.')) {
      expireMutation.mutate(id);
    }
  };

  // Filter by search query
  const filteredFlyers = useMemo(() => {
    if (!searchQuery.trim()) {
      return flyers;
    }
    const query = searchQuery.toLowerCase();
    return flyers.filter(f =>
      f.name.toLowerCase().includes(query) ||
      formatDate(f.validFrom).toLowerCase().includes(query) ||
      formatDate(f.validTo).toLowerCase().includes(query)
    );
  }, [flyers, searchQuery]);

  const handleViewPdf = async (flyerId: string) => {
    try {
      // Fetch and display PDF from database
      const blob = await flyersService.getPdfBlob(flyerId, true);
      const url = URL.createObjectURL(blob);
      window.open(url, '_blank');
    } catch (error) {
      console.error('Error viewing PDF:', error);
      alert('Chyba při zobrazení PDF');
    }
  };

  if (isLoading) {
    return (
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-center py-12">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
      <div className="mb-6">
        <h1 className="text-3xl font-bold text-gray-900">Letáky</h1>
        <p className="mt-2 text-gray-600">
          {jeProdejce
            ? 'Prohlížejte aktivní propagační letáky'
            : isArchive
              ? 'Archiv vypršelých letáků (pouze k nahlédnutí)'
              : activeTab === 'active'
                ? 'Prohlížejte aktivní propagační letáky'
                : 'Letáky dodavatelů napříč značkami – pouze k nahlédnutí'}
        </p>
      </div>

      {!jeProdejce && (
        <div className="mb-6 border-b border-gray-200">
          <nav className="-mb-px flex flex-wrap gap-x-6">
            {ZALOZKY_PREHLED.map((z) => {
              const vybrana = activeTab === z;
              return (
                <button
                  key={z}
                  onClick={() => setActiveTab(z)}
                  className={`pb-4 px-1 border-b-2 font-medium text-sm transition-colors whitespace-nowrap ${
                    vybrana
                      ? 'border-blue-500 text-blue-600'
                      : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                  }`}
                >
                  {ZALOZKA_POPISKY[z]}
                  {/* Dokud se nenacte, ukazat tecky - nula by vypadala jako platny vysledek. */}
                  <span
                    className={`ml-2 py-0.5 px-2 rounded-full text-xs ${
                      vybrana ? 'bg-blue-100 text-blue-700' : 'bg-gray-100'
                    }`}
                  >
                    {loadingPrehled ? '…' : pocty[z] ?? 0}
                  </span>
                </button>
              );
            })}
          </nav>
        </div>
      )}

      {/* Search bar */}
      <div className="mb-6">
        <div className="relative">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
          <Input
            type="text"
            placeholder="Hledat podle názvu nebo data platnosti..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="pl-10"
          />
        </div>
      </div>

      {filteredFlyers.length === 0 ? (
        <div className="bg-white rounded-lg shadow p-12 text-center">
          <div className="text-gray-400 mb-4">
            {searchQuery ? (
              <Search className="w-16 h-16 mx-auto" />
            ) : (
              <FileText className="w-16 h-16 mx-auto" />
            )}
          </div>
          {searchQuery ? (
            <>
              <h3 className="text-lg font-medium text-gray-900 mb-2">Žádné výsledky</h3>
              <p className="text-gray-600 mb-6">Zkuste změnit vyhledávací dotaz</p>
              <Button variant="outline" onClick={() => setSearchQuery('')}>
                Vymazat vyhledávání
              </Button>
            </>
          ) : isArchive ? (
            <>
              <h3 className="text-lg font-medium text-gray-900 mb-2">Žádné vypršelé letáky</h3>
              <p className="text-gray-600">Archiv je prázdný</p>
            </>
          ) : activeTab === 'draft' ? (
            <>
              <h3 className="text-lg font-medium text-gray-900 mb-2">Žádné koncepty</h3>
              <p className="text-gray-600">Rozpracované letáky dodavatelů se objeví tady.</p>
            </>
          ) : activeTab === 'ceka' ? (
            <>
              <h3 className="text-lg font-medium text-gray-900 mb-2">Nic nečeká na schválení</h3>
              <p className="text-gray-600">Odeslané letáky se objeví tady.</p>
            </>
          ) : activeTab === 'rejected' ? (
            <>
              <h3 className="text-lg font-medium text-gray-900 mb-2">Žádné zamítnuté letáky</h3>
              <p className="text-gray-600">Letáky vrácené dodavateli k opravě se objeví tady.</p>
            </>
          ) : (
            <>
              <h3 className="text-lg font-medium text-gray-900 mb-2">Žádné aktivní letáky</h3>
              <p className="text-gray-600">Momentálně nejsou k dispozici žádné aktivní letáky</p>
            </>
          )}
        </div>
      ) : (
        <div className="bg-white rounded-lg shadow divide-y">
          {filteredFlyers.map(flyer => (
            <div key={flyer.id} className="p-6 hover:bg-gray-50">
              <div className="flex items-start justify-between">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center space-x-3 mb-2 min-w-0">
                    {/* Stav neopakujeme štítkem - říká ho vybraná záložka. */}
                    <h3 className="text-lg font-semibold truncate min-w-0" title={flyer.name}>{flyer.name}</h3>
                  </div>
                  <div className="text-sm text-gray-600 space-y-1">
                    <div>Platnost: {formatDate(flyer.validFrom)} - {formatDate(flyer.validTo)}</div>
                    <div>
                      Stránky: {flyer.pages?.length || 0} | Produkty:{' '}
                      {flyer.productCount ??
                        (flyer.pages?.reduce((s, p) => s + (p.slots?.filter(slot => slot && slot.type === 'product').length || 0), 0) || 0)}
                    </div>
                    {/* Přehled jde napříč dodavateli, proto u letáku i jeho autor. */}
                    {!jeProdejce && flyer.supplier && (
                      <div>
                        Dodavatel: {[flyer.supplier.firstName, flyer.supplier.lastName].filter(Boolean).join(' ') || flyer.supplier.email}
                      </div>
                    )}
                    <div>Vytvořeno: {formatDate(flyer.createdAt)}</div>
                  </div>
                </div>
                <div className="flex space-x-2 flex-shrink-0">
                  {/* Náhled jen ke čtení - na rozdíl od PDF funguje i u rozpracovaného
                      konceptu. Prodejce ho nemá, ten vidí jen cizí aktivní letáky. */}
                  {!jeProdejce && (
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => navigate(`/active-flyers/${flyer.id}`)}
                      title="Náhled letáku podle aktuálních dat (bez úprav)"
                    >
                      <Eye className="w-4 h-4 mr-1" />
                      Náhled
                    </Button>
                  )}
                  {/* Koncept, který nikdy nebyl odeslán, uložené PDF nemá - tlačítko
                      by skončilo chybou, proto se u něj nenabízí. */}
                  {flyer.hasPdf !== false && (
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => handleViewPdf(flyer.id)}
                      title="Uložené PDF – snímek z posledního generování"
                    >
                      <FileText className="w-4 h-4 mr-1" />
                      PDF
                    </Button>
                  )}
                  {/* Akce jen u aktivních letáků; jinde je pohled read-only. */}
                  {muzeSpravovat && activeTab === 'active' && (
                    <Button
                      size="sm"
                      variant="primary"
                      onClick={() => handleRegeneratePdf(flyer.id)}
                      isLoading={regenerateMutation.isPending}
                      disabled={regenerateMutation.isPending}
                      title="Vytvoří PDF znovu z aktuálních dat a nahradí jím uložené – například kvůli vodoznaku VYPRODÁNO. Ceny ani platnost se tím nemění."
                    >
                      <RefreshCw className="w-4 h-4 mr-1" />
                      {regenerateMutation.isPending ? 'Generuji…' : 'Přegenerovat PDF'}
                    </Button>
                  )}
                  {/* Ukončit platnost – schvalovatel a admin, jen u aktivních. */}
                  {muzeSpravovat && activeTab === 'active' && (
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => handleExpire(flyer.id)}
                      isLoading={expireMutation.isPending}
                      disabled={expireMutation.isPending}
                      title="Předčasně ukončí platnost letáku – přesune se do Archivu a prodejci ho přestanou vidět. Produkty se tím uvolní k úpravám."
                      className="border-red-500 text-red-600 hover:bg-red-50"
                    >
                      <XCircle className="w-4 h-4 mr-1" />
                      Ukončit platnost
                    </Button>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};
